import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { supabase } from '../lib/supabase';
import Header from '../components/Header';

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

const PLAN_LABELS = { gratis: 'Grátis', basico: 'Básico', pro: 'Profissional', admin: 'Admin' };

function AdminUsuarios() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterConsent, setFilterConsent] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    setErrorMsg('');
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, user_id, nome, email, whatsapp, plano, aceita_email_marketing, created_at')
        .order('created_at', { ascending: false });

      if (error) {
        setErrorMsg('Erro ao carregar usuários: ' + error.message);
        setUsers([]);
      } else {
        setUsers(data || []);
      }
    } catch {
      setErrorMsg('Erro de conexão com o banco de dados.');
      setUsers([]);
    }
    setLoadingUsers(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filtered = useMemo(() => {
    let result = [...users];
    if (searchEmail) {
      const s = searchEmail.toLowerCase();
      result = result.filter((u) => u.email?.toLowerCase().includes(s) || u.nome?.toLowerCase().includes(s));
    }
    if (filterPlan) {
      if (filterPlan === 'sem_plano') {
        result = result.filter((u) => !u.plano);
      } else {
        result = result.filter((u) => u.plano === filterPlan);
      }
    }
    if (filterConsent === 'email') {
      result = result.filter((u) => u.aceita_email_marketing);
    } else if (filterConsent === 'none') {
      result = result.filter((u) => !u.aceita_email_marketing);
    }
    return result;
  }, [users, searchEmail, filterPlan, filterConsent]);

  // KPIs
  const total = users.length;
  const withWhatsapp = users.filter((u) => u.whatsapp).length;
  const consentEmail = users.filter((u) => u.aceita_email_marketing).length;
  const withPlan = users.filter((u) => u.plano).length;

  const handleExport = () => {
    const wsData = [
      ['Nome', 'Email', 'WhatsApp', 'Plano', 'Email Mkt', 'Criado em'],
      ...filtered.map((u) => [
        u.nome || '',
        u.email || '',
        u.whatsapp || '',
        PLAN_LABELS[u.plano] || u.plano || 'Sem plano',
        u.aceita_email_marketing ? 'Sim' : 'Não',
        formatDate(u.created_at),
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 25 }, { wch: 28 }, { wch: 18 }, { wch: 14 }, { wch: 10 }, { wch: 20 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Usuários');
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `QuantoCustaObra_Usuarios_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6 md:py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Usuários</h1>
            <p className="text-gray-500">{filtered.length} de {total} usuários</p>
          </div>
          <div className="flex gap-2 mt-3 sm:mt-0">
            <button onClick={() => navigate('/admin/dashboard')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">Dashboard</button>
            <button onClick={() => navigate('/admin/logs')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">Logs</button>
            <button onClick={() => navigate('/admin/feedbacks')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">Feedbacks</button>
            <button onClick={() => navigate('/admin/cub')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">CUB</button>
            <button onClick={() => navigate('/admin/sinapi')} className="px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">SINAPI</button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{total}</p>
            <p className="text-xs text-gray-400 mt-1">usuários cadastrados</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">WhatsApp</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{withWhatsapp}</p>
            <p className="text-xs text-gray-400 mt-1">com número informado</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Email Mkt</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{consentEmail}</p>
            <p className="text-xs text-gray-400 mt-1">aceitam email marketing</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Com Plano</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{withPlan}</p>
            <p className="text-xs text-gray-400 mt-1">escolheram um plano</p>
          </div>
        </div>

        {/* Error message */}
        {errorMsg && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
            {errorMsg}
          </div>
        )}

        {/* Filters + Table */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-4 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 flex-1">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Buscar</label>
                  <input
                    type="text"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    placeholder="Nome ou email..."
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Plano</label>
                  <select
                    value={filterPlan}
                    onChange={(e) => setFilterPlan(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Todos</option>
                    <option value="sem_plano">Sem plano</option>
                    <option value="gratis">Grátis</option>
                    <option value="basico">Básico</option>
                    <option value="pro">Profissional</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Consentimento</label>
                  <select
                    value={filterConsent}
                    onChange={(e) => setFilterConsent(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Todos</option>
                    <option value="email">Aceita Email</option>
                    <option value="none">Nenhum consentimento</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={fetchUsers}
                  disabled={loadingUsers}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors disabled:opacity-50"
                >
                  {loadingUsers ? 'Carregando...' : 'Atualizar'}
                </button>
                <button
                  onClick={handleExport}
                  disabled={filtered.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Exportar Excel
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loadingUsers ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left">
                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Nome</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">WhatsApp</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Plano</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider text-center">Email Mkt</th>
                    <th className="px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Criado em</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-gray-400">Nenhum usuário encontrado</td>
                    </tr>
                  ) : (
                    filtered.map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900 max-w-[150px] truncate">{u.nome || '—'}</td>
                        <td className="px-4 py-3 text-gray-700 max-w-[200px] truncate">{u.email}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{u.whatsapp || <span className="text-gray-300">—</span>}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            u.plano ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {PLAN_LABELS[u.plano] || u.plano || 'Sem plano'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {u.aceita_email_marketing ? (
                            <span className="inline-block w-5 h-5 bg-green-100 text-green-600 rounded-full text-xs leading-5 font-bold">✓</span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDate(u.created_at)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminUsuarios;
